import { encryptData, decryptData, initDB } from "@/service/indexedDBService";

/* ══════════════════════════════════════════════════════════════════════
   Journal local des ventes comptabilisées - store "comptabilisationvente"

   Une facture par enregistrement, rattachée à une journée de caisse.
   Écrit au moment de la comptabilisation (saveItems), après acceptation
   par le serveur : ce journal ne contient donc que des ventes réellement
   passées en comptabilité, jamais des commandes en attente.

   Conventions du store (identiques aux stores sg* / sv*) :
     - clé primaire "k" EN CLAIR → db.get() direct en O(1)
     - champs de filtrage et totaux en clair → agrégation sans déchiffrer
     - lignes d'articles chiffrées dans "blob"
     - aucun champ nommé "clé" (getData/deleteData le déchiffreraient)

   Rétention : 9 jours de caisse. Voir purgeComptabilisationVente().
   ══════════════════════════════════════════════════════════════════════ */

const STORE = 'comptabilisationvente';
const RETENTION_JOURS = 9;

// Connexion partagée : voir la note dans orderQueueService.getDB().
async function getDB() {
    return initDB();
}

/* Normalise une date (YYYY-MM-DD, "YYYY-MM-DD HH:mm:ss", DD/MM/YYYY,
   timestamp, Date) en YYYY-MM-DD. Ce format se compare lexicographiquement,
   ce qui permet de purger par simple comparaison de chaînes. */
export function toJourISO(v) {
    if (!v && v !== 0) return '';
    if (v instanceof Date) return isNaN(v) ? '' : dateToISO(v);
    if (typeof v === 'number') {
        const d = new Date(v);
        return isNaN(d) ? '' : dateToISO(d);
    }
    const s = String(v).trim();
    if (!s) return '';
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    const m2 = s.match(/^(\d{2})\/(\d{2})\/(\d{2,4})/);
    if (m2) {
        let y = m2[3];
        if (y.length === 2) y = '20' + y;
        return `${y}-${m2[2]}-${m2[1]}`;
    }
    const d = new Date(s);
    return isNaN(d) ? '' : dateToISO(d);
}

function dateToISO(d) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
}

function decaleJourISO(iso, n) {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + n);
    return dateToISO(dt);
}

/* Clé primaire : une facture est unique pour un couple (caisse, journée).
   Réécrire la même facture met à jour l'enregistrement au lieu d'en créer
   un second - l'opération est donc rejouable sans risque de doublon. */
export function buildKey(pidcaisse, caissejour, srvdocnum) {
    return `${pidcaisse}|${caissejour}|${srvdocnum}`;
}

/**
 * Enregistre une vente comptabilisée.
 *
 * @param {object} v
 * @param {string} v.pidcaisse      pid de la caisse (caisseinfo.cpid)
 * @param {string} v.caissejour     journée de caisse (date d'ouverture)
 * @param {string} v.srvdocnum      numéro de document serveur
 * @param {string} v.srvpid         pid du document serveur
 * @param {string} v.pidindexedb    numéro fourni par IndexedDB
 * @param {string} [v.commandetable] table (restaurant)
 * @param {string} [v.serveur]       serveur en salle (restaurant)
 * @param {string} [v.client]
 * @param {object} [v.mtnt]          totaux (ht, remise, ttc, reçu)
 * @param {Array}  [v.items]         lignes d'articles → chiffrées
 * @param {object} [v.extra]         complément libre → chiffré
 */
export async function enregistrerVenteComptabilisee(v) {
    try {
        const caissejour = toJourISO(v.caissejour);
        const pidcaisse  = String(v.pidcaisse || '');
        const srvdocnum  = String(v.srvdocnum || '');

        if (!pidcaisse || !caissejour || !srvdocnum) {
            console.warn('[comptabilisationvente] Enregistrement ignoré, clé incomplète', {
                pidcaisse, caissejour, srvdocnum,
            });
            return { success: false, message: 'Clé incomplète' };
        }

        const record = {
            k:              buildKey(pidcaisse, caissejour, srvdocnum),
            scope:          `${pidcaisse}|${caissejour}`,
            pidcaisse,
            caissejour,
            srvdocnum,
            srvpid:         String(v.srvpid      || ''),
            pidindexedb:    String(v.pidindexedb || ''),
            commandetable:  String(v.commandetable || ''),
            serveur:        String(v.serveur     || ''),
            client:         String(v.client      || ''),
            mtnt:           v.mtnt || {},
            nbLignes:       Array.isArray(v.items) ? v.items.length : 0,
            comptabiliseAt: Date.now(),
            blob:           encryptData({ items: v.items || [], extra: v.extra || {} }),
        };

        const db = await getDB();
        const tx = db.transaction(STORE, "readwrite");
        await tx.objectStore(STORE).put(record);   // put = idempotent sur la clé
        await tx.done;
        return { success: true, k: record.k };
    } catch (e) {
        console.warn('[comptabilisationvente] Échec enregistrement:', e);
        return { success: false, message: String(e && e.message || e) };
    }
}

/* Ventes d'une journée de caisse. items reste chiffré sauf avecItems=true. */
export async function getVentesDuJour(pidcaisse, caissejour, avecItems = false) {
    const scope = `${pidcaisse}|${toJourISO(caissejour)}`;
    const db = await getDB();
    const tx = db.transaction(STORE, "readonly");
    const rows = await tx.objectStore(STORE).index('scopeIndex').getAll(scope);
    await tx.done;
    return rows
        .sort((a, b) => (a.comptabiliseAt || 0) - (b.comptabiliseAt || 0))
        .map(r => hydrate(r, avecItems));
}

/* Toutes les ventes d'une caisse, journées confondues (dans la rétention). */
export async function getVentesParCaisse(pidcaisse, avecItems = false) {
    const db = await getDB();
    const tx = db.transaction(STORE, "readonly");
    const rows = await tx.objectStore(STORE).index('pidcaisseIndex').getAll(String(pidcaisse));
    await tx.done;
    return rows
        .sort((a, b) => (b.comptabiliseAt || 0) - (a.comptabiliseAt || 0))
        .map(r => hydrate(r, avecItems));
}

export async function getVenteParDoc(pidcaisse, caissejour, srvdocnum, avecItems = true) {
    const db = await getDB();
    const tx = db.transaction(STORE, "readonly");
    const row = await tx.objectStore(STORE).get(buildKey(pidcaisse, toJourISO(caissejour), srvdocnum));
    await tx.done;
    return row ? hydrate(row, avecItems) : null;
}

/* Totaux d'une journée, calculés sans déchiffrer : les montants sont en clair. */
export async function getTotauxDuJour(pidcaisse, caissejour) {
    const rows = await getVentesDuJour(pidcaisse, caissejour, false);
    return rows.reduce((acc, r) => {
        const m = r.mtnt || {};
        acc.nbFactures += 1;
        acc.totalTTC   += parseFloat(m.docmtnttotalttc || 0) || 0;
        acc.totalHT    += parseFloat(m.docmtnttotalht  || 0) || 0;
        acc.totalRemise += parseFloat(m.docmtntremise  || 0) || 0;
        return acc;
    }, { nbFactures: 0, totalTTC: 0, totalHT: 0, totalRemise: 0 });
}

/* Journées de caisse présentes, de la plus récente à la plus ancienne. */
export async function getJournees(pidcaisse = null) {
    const db = await getDB();
    const tx = db.transaction(STORE, "readonly");
    const rows = await tx.objectStore(STORE).getAll();
    await tx.done;
    const map = new Map();
    for (const r of rows) {
        if (pidcaisse && String(r.pidcaisse) !== String(pidcaisse)) continue;
        const cle = r.scope;
        if (!map.has(cle)) {
            map.set(cle, { pidcaisse: r.pidcaisse, caissejour: r.caissejour, nbFactures: 0 });
        }
        map.get(cle).nbFactures++;
    }
    return Array.from(map.values()).sort((a, b) => (a.caissejour < b.caissejour ? 1 : -1));
}

/**
 * Rétention : supprime les journées de caisse plus vieilles que 9 jours.
 * La comparaison est lexicographique sur YYYY-MM-DD, format qui s'ordonne
 * comme la date elle-même. À appeler au boot (runBootMaintenance).
 */
export async function purgeComptabilisationVente(retentionJours = RETENTION_JOURS) {
    try {
        const limite = decaleJourISO(dateToISO(new Date()), -retentionJours);
        const db = await getDB();
        const tx = db.transaction(STORE, "readwrite");
        const store = tx.objectStore(STORE);
        let cursor = await store.openCursor();
        let deleted = 0;
        while (cursor) {
            const jour = cursor.value.caissejour;
            // Un enregistrement sans journée exploitable est supprimé : il
            // échapperait sinon à toute rétention.
            if (!jour || jour < limite) { cursor.delete(); deleted++; }
            cursor = await cursor.continue();
        }
        await tx.done;
        return deleted;
    } catch (e) {
        console.warn('[comptabilisationvente] Erreur purge:', e);
        return 0;
    }
}

/* Retire "blob" du retour - chiffré, il n'a aucun intérêt pour l'appelant - et le remplace par son contenu déchiffré quand avecItems est demandé. */
function hydrate(r, avecItems) {
    const reste = { ...r };
    delete reste.blob;
    if (!avecItems) return reste;

    let items = [], extra = {};
    try {
        const d = decryptData(r.blob) || {};
        items = d.items || [];
        extra = d.extra || {};
    } catch (e) {
        console.warn('[comptabilisationvente] Blob illisible pour', r.k);
    }
    return { ...reste, items, extra };
}
